import React from 'react';
import {
  Card, Grid, Stack, Text,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { PropertyUnit } from '../../../providers/api/dtos';
import UpsertPropertyUnitAction from '../upsert-property-unit-action/UpsertPropertyUnitAction';
import DeletePropertyUnitAction from '../delete-property-unit-action/DeletePropertyUnitAction';
import { propertyUnitIntendedUseLabels } from '../../../labels';
import { NO_VALUE_SYMBOL } from '../../utils';
import { removeNullAndUndefinedValues } from '../../../utils/array';

export interface ViewPropertyUnitCardProps {
  propertyUnit: PropertyUnit,
  canDelete?: boolean,
  canUpdate?: boolean,
  onUpdate?: (updatedPropertyUnit: PropertyUnit, oldPropertyUnit: PropertyUnit) => void,
  onDelete?: (deletedPropertyUnit: PropertyUnit) => void,
}

export const LOAD_LEAD_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati.';

const ViewPropertyUnitCard: React.FC<ViewPropertyUnitCardProps> = ({
  propertyUnit,
  canDelete = false,
  canUpdate = false,
  onDelete,
  onUpdate,
}) => (
  <Card aria-label="Card dell'unità immobiliare">
    <Card.Header
      title="Unità immobiliare"
      primaryActions={removeNullAndUndefinedValues([
        canUpdate && (
          <UpsertPropertyUnitAction
            propertyUnit={propertyUnit}
            onSuccess={onUpdate}
          />
        ),
        canDelete && (
          <DeletePropertyUnitAction
            propertyUnit={propertyUnit}
            onSuccess={onDelete}
          />
        ),
      ])}
    />
    <Card.Content>
      <Grid gutter={150}>
        <Grid.Unit size={1 / 4}>
          <Stack>
            <Text.Mini transform="uppercase">
              Sezione
            </Text.Mini>
            <Text.Caption>
              {propertyUnit.section ?? NO_VALUE_SYMBOL}
            </Text.Caption>
          </Stack>
        </Grid.Unit>
        <Grid.Unit size={1 / 4}>
          <Stack>
            <Text.Mini transform="uppercase">
              Foglio
            </Text.Mini>
            <Text.Caption>
              {propertyUnit.sheet}
            </Text.Caption>
          </Stack>
        </Grid.Unit>
        <Grid.Unit size={1 / 4}>
          <Stack>
            <Text.Mini transform="uppercase">
              Particella
            </Text.Mini>
            <Text.Caption>
              {propertyUnit.parcel}
            </Text.Caption>
          </Stack>
        </Grid.Unit>
        <Grid.Unit size={1 / 4}>
          <Stack>
            <Text.Mini transform="uppercase">
              Subalterno
            </Text.Mini>
            <Text.Caption>
              {propertyUnit.subunit ?? NO_VALUE_SYMBOL}
            </Text.Caption>
          </Stack>
        </Grid.Unit>
        <Grid.Unit size={1 / 4}>
          <Stack>
            <Text.Mini transform="uppercase">
              Categoria
            </Text.Mini>
            <Text.Caption>
              {propertyUnit.category}
            </Text.Caption>
          </Stack>
        </Grid.Unit>
        <Grid.Unit size={2 / 4}>
          <Stack>
            <Text.Mini transform="uppercase">
              Destinazione d&apos;uso
            </Text.Mini>
            <Text.Caption>
              {propertyUnit.intendedUse && propertyUnitIntendedUseLabels[propertyUnit.intendedUse]}
            </Text.Caption>
          </Stack>
        </Grid.Unit>
        <Grid.Unit size={1 / 4}>
          <Stack>
            <Text.Mini transform="uppercase">
              Rendita
            </Text.Mini>
            <Text.Caption>
              {propertyUnit.value ? formatEuro(propertyUnit.value) : NO_VALUE_SYMBOL}
            </Text.Caption>
          </Stack>
        </Grid.Unit>
        <Grid.Unit>
          <Stack>
            <Text.Mini transform="uppercase">
              Classe energetica
            </Text.Mini>
            <Text.Caption>
              {propertyUnit.energyClass ?? NO_VALUE_SYMBOL}
            </Text.Caption>
          </Stack>
        </Grid.Unit>
      </Grid>
    </Card.Content>
  </Card>
);

export default ViewPropertyUnitCard;
